import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase, supabasePublic, getEmailConfirmRedirect } from '../lib/supabase';
import { clearAppCache } from '../lib/storage';
import { signInWithOAuthNative, OAUTH_CANCELLED } from '../lib/capacitorAuth';
import { prepareFileForUpload } from '../lib/fileUtils';
import { identifyUser, resetUser } from '../lib/posthog';
import { registerForPushNotifications, removeExternalUserId } from '../lib/onesignal';

export const AuthContext = createContext(null);

const PROFILE_CACHE_KEY = 'tilted_cached_profile';

const getCachedProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const setCachedProfile = (profile) => {
  try {
    if (profile) localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch { /* iOS private browsing */ }
};

// Reserved usernames that only admins can use
const RESERVED_USERNAMES = [
  'admin', 'administrator', 'mod', 'moderator', 'gettilted', 'official',
  'support', 'help', 'info', 'contact', 'team', 'staff', 'owner', 'founder',
  'ceo', 'management', 'system', 'root', 'superuser', 'verified', 'vip'
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ageVerified, setAgeVerified] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  // Check if username is reserved
  const isUsernameReserved = (username) => {
    return RESERVED_USERNAMES.includes(username.toLowerCase().trim());
  };

  // Check age from birthdate
  const calculateAge = (birthdate) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Register push notifications for the current user
  const registerPushNotifications = (userId) => {
    registerForPushNotifications(userId);
  };

  // Fetch user profile from profiles table with timeout
  const fetchProfile = async (userId) => {
    console.log('Fetching profile for user:', userId);

    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        console.warn('Profile fetch timed out');
        resolve(null);
      }, 10000);
    });

    const fetchPromise = (async () => {
      try {
        const profileColumns = 'id, username, display_name, is_admin, created_at, birthdate, age_verified, avatar_url, tos_accepted_at, hometown_metro_id';
        let { data, error } = await supabasePublic
          .from('profiles')
          .select(profileColumns)
          .eq('id', userId)
          .single();

        if (error && /hometown_metro_id/i.test(error.message || '')) {
          const fallback = await supabasePublic
            .from('profiles')
            .select('id, username, display_name, is_admin, created_at, birthdate, age_verified, avatar_url, tos_accepted_at')
            .eq('id', userId)
            .single();
          data = fallback.data;
          error = fallback.error;
        }

        if (error) {
          console.error('Profile fetch error:', error);
          return null;
        }

        console.log('Profile fetched successfully:', data?.username);
        return data;
      } catch (error) {
        console.error('Profile fetch exception:', error);
        return null;
      }
    })();

    return Promise.race([fetchPromise, timeoutPromise]);
  };

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    let profileFetchedForUserId = null;
    
    const setProfileForUser = async (session, event) => {
      const userId = session.user.id;
      
      // Skip duplicate fetches for the same event cycle, but always
      // re-fetch on TOKEN_REFRESHED (app resumed from background)
      if (profileFetchedForUserId === userId && event !== 'TOKEN_REFRESHED') {
        console.log('AuthContext: Profile already loaded for', userId, '- skipping', event);
        return;
      }
      
      console.log('AuthContext: Fetching profile for', event);
      let userProfile = await fetchProfile(userId);

      if (!isMounted) return;

      if (userProfile && userProfile.username) {
        console.log('AuthContext: Profile set -', userProfile.username, 'avatar:', !!userProfile.avatar_url, 'admin:', userProfile.is_admin);
        profileFetchedForUserId = userId;
        setProfile(userProfile);
        setCachedProfile(userProfile);
        setAgeVerified(userProfile?.age_verified || false);
        identifyUser(userId, {
          username: userProfile.username,
          display_name: userProfile.display_name,
          email: session.user.email,
        });
      } else {
        console.warn('AuthContext: Profile fetch failed after', event, '- setting fallback');
        const meta = session.user.user_metadata || {};
        // Don't mark as fetched — allow subsequent events to retry
        setProfile({
          id: userId,
          username: meta.username || session.user.email?.split('@')[0] || 'user',
          display_name: meta.display_name || meta.full_name || meta.name || session.user.email?.split('@')[0] || 'User',
          avatar_url: null,
          is_admin: false,
          created_at: new Date().toISOString()
        });
        setAgeVerified(meta.age_verified || false);

        // Retry in background for any event (not just SIGNED_IN)
        const retryDelays = [500, 2000, 5000];
        for (const delay of retryDelays) {
          await new Promise(r => setTimeout(r, delay));
          if (!isMounted) return;
          const retryProfile = await fetchProfile(userId);
          if (retryProfile?.username) {
            console.log('AuthContext: Background retry succeeded -', retryProfile.username);
            profileFetchedForUserId = userId;
            setProfile(retryProfile);
            setCachedProfile(retryProfile);
            setAgeVerified(retryProfile?.age_verified || false);
            return;
          }
        }
        console.warn('AuthContext: All profile retries failed');
        profileFetchedForUserId = userId;
      }
    };
    
    const initAuth = async () => {
      console.log('AuthContext: Initializing');

      // Show cached profile instantly while auth resolves in background
      const cached = getCachedProfile();
      if (cached) {
        console.log('AuthContext: Loaded cached profile -', cached.username);
        setProfile(cached);
        setAgeVerified(cached.age_verified || false);
        setLoading(false);
      }
      
      const forceTimeout = setTimeout(() => {
        if (isMounted && loading) {
          console.warn('AuthContext: Force completing after timeout');
          setLoading(false);
        }
      }, 5000);
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          if (error) console.warn('Auth session error:', error.message);
          else console.log('AuthContext: No session found');
          if (!session) {
            setCachedProfile(null);
            setProfile(null);
          }
          clearTimeout(forceTimeout);
          if (isMounted) setLoading(false);
          return;
        }
        
        console.log('AuthContext: Session found for', session.user.email);
        if (!isMounted) { clearTimeout(forceTimeout); return; }
        
        setUser(session.user);
        await setProfileForUser(session, 'init');
        // Register push notifications on app launch if already signed in
        registerPushNotifications(session.user.id);

        clearTimeout(forceTimeout);
        if (isMounted) setLoading(false);
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearTimeout(forceTimeout);
        if (isMounted) setLoading(false);
      }
    };
    
    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event, 'user:', session?.user?.id);
        if (!isMounted) return;
        
        if (session?.user) {
          setUser(session.user);
          
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
            await setProfileForUser(session, event);
            if (isMounted) setLoading(false);
          }

          if (event === 'SIGNED_IN') {
            logActivity(session.user.id, 'login').catch(console.error);
            // Register push notifications after login
            registerPushNotifications(session.user.id);
          }
        } else {
          console.log('AuthContext: No session, clearing user state');
          profileFetchedForUserId = null;
          setUser(null);
          setProfile(null);
          setAgeVerified(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Periodic session check - every 5 minutes, verify session and refresh profile
  useEffect(() => {
    if (!user) return;
    
    const checkSession = async () => {
      console.log('AuthContext: Periodic session check...');
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.warn('AuthContext: Session lost during periodic check!');
          // Try to refresh
          const { data, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error('AuthContext: Could not refresh session:', refreshError);
            setUser(null);
            setProfile(null);
            return;
          }
          console.log('AuthContext: Session refreshed successfully');
        }
        
        // Always refresh profile to ensure we have latest data
        if (session?.user) {
          const userProfile = await fetchProfile(session.user.id);
          if (userProfile) {
            setProfile(userProfile);
            setCachedProfile(userProfile);
            setAgeVerified(userProfile?.age_verified || false);
          }
        }
      } catch (err) {
        console.error('AuthContext: Error during periodic check:', err);
      }
    };
    
    // Check every 5 minutes
    const interval = setInterval(checkSession, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user]);

  // Auto-detect corrupted state: user has email but no proper username from DB
  const hasAttemptedAutoClear = useRef(false);
  useEffect(() => {
    // Only check once per session to prevent loops
    if (hasAttemptedAutoClear.current) return;
    
    // Wait for both user and profile to be set
    if (!user || !profile || loading) return;
    
    // Check for signs of corrupted state:
    // 1. Username matches email prefix (fallback was used)
    // 2. Profile exists but username is suspiciously similar to email
    const emailPrefix = user.email?.split('@')[0]?.toLowerCase();
    const username = profile?.username?.toLowerCase();
    
    // If username matches email prefix exactly, profile might not have loaded from DB
    if (username && emailPrefix && username === emailPrefix) {
      console.warn('AuthContext: Detected potential corrupted state - username matches email prefix');
      console.log('Email prefix:', emailPrefix);
      console.log('Username:', username);
      
      // Check if we have a proper profile in the database
      const checkRealProfile = async () => {
        try {
          const { data: realProfile, error } = await supabase
            .from('profiles')
            .select('username, display_name')
            .eq('id', user.id)
            .single();
          
          if (error) {
            console.error('AuthContext: Error checking real profile:', error);
            return;
          }
          
          // If real profile has a different username, we have corrupted cache
          if (realProfile?.username && realProfile.username.toLowerCase() !== emailPrefix) {
            console.warn('AuthContext: Confirmed corrupted cache! Real username:', realProfile.username);
            console.log('Cached username:', username);
            console.log('Auto-clearing cache to fix...');
            
            hasAttemptedAutoClear.current = true;
            
            // Auto-clear cache (will preserve auth)
            setTimeout(() => {
              clearAppCache();
            }, 500);
          } else {
            console.log('AuthContext: Username matches, state is OK');
          }
        } catch (err) {
          console.error('AuthContext: Error during corruption check:', err);
        }
      };
      
      checkRealProfile();
    }
  }, [user, profile, loading]);

  // Re-fetch profile when app becomes visible again (user returns to tab/app)
  useEffect(() => {
    if (!user) return;

    const refreshProfileOnResume = async () => {
      console.log('AuthContext: App became visible, checking session...');

      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          const userProfile = await fetchProfile(session.user.id);
          if (userProfile) {
            console.log('AuthContext: Refreshed profile on resume');
            setProfile(userProfile);
            setCachedProfile(userProfile);
            setAgeVerified(userProfile?.age_verified || false);
          }
        } else {
          console.warn('AuthContext: Session lost while app was hidden');
          // Try to refresh session
          const { data, error } = await supabase.auth.refreshSession();
          if (error) {
            console.error('AuthContext: Could not restore session');
            setUser(null);
            setProfile(null);
          }
        }
      } catch (err) {
        console.error('AuthContext: Error on resume:', err);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProfileOnResume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // On iOS native, visibilitychange doesn't fire reliably on app resume
    let appStateHandle;
    if (Capacitor.isNativePlatform()) {
      appStateHandle = App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          refreshProfileOnResume();
        }
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (appStateHandle) {
        appStateHandle.then(h => h.remove());
      }
    };
  }, [user]);

  // Log user activity
  const logActivity = async (userId, action, entityType = null, entityId = null, metadata = {}) => {
    try {
      await supabase.from('activity_log').insert({
        user_id: userId,
        action,
        entity_type: entityType,
        entity_id: entityId,
        metadata
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  // Sign up with email/password (with age verification)
  const signUp = async (email, password, username, displayName, birthdate) => {
    // Check reserved username
    if (isUsernameReserved(username)) {
      throw new Error('This username cannot be used. Please choose another.');
    }

    // Check age
    const age = calculateAge(birthdate);
    if (age < 21) {
      throw new Error('You must be 21 or older to use Tilted.');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getEmailConfirmRedirect(),
        data: {
          username,
          display_name: displayName,
          birthdate,
          age_verified: true
        }
      }
    });

    if (error) throw error;
    
    // Mark as new user for PWA prompt
    setIsNewUser(true);
    
    // Process referral if exists
    if (data?.user?.id) {
      processReferral(data.user.id);

      // Persist TOS acceptance for email signups (they checked the TOS checkbox)
      supabase
        .from('profiles')
        .update({ tos_accepted_at: new Date().toISOString() })
        .eq('id', data.user.id)
        .then(({ error }) => {
          if (error) console.warn('Could not set tos_accepted_at on signup:', error);
        });
    }

    return data;
  };

  // Process referral code after signup
  const processReferral = async (newUserId) => {
    try {
      const referralCode = sessionStorage.getItem('tilted_referral');
      
      if (!referralCode) return;
      
      console.log('Processing referral code:', referralCode);
      
      // Find referrer by code
      const { data: referrer, error: referrerError } = await supabase
        .from('profiles')
        .select('id')
        .eq('referral_code', referralCode)
        .single();

      if (referrerError || !referrer) {
        console.log('Referrer not found for code:', referralCode);
        sessionStorage.removeItem('tilted_referral');
        return;
      }

      // Update new user's profile with referred_by
      await supabase
        .from('profiles')
        .update({ referred_by: referrer.id })
        .eq('id', newUserId);

      // Increment referrer's count
      try {
        await supabase.rpc('increment_referral_count', { 
          user_id: referrer.id 
        });
      } catch (e) {
        console.log('increment_referral_count function not available yet');
      }

      // Check and award badges to referrer
      try {
        await supabase.rpc('check_and_award_badges', {
          p_user_id: referrer.id
        });
      } catch (e) {
        console.log('check_and_award_badges function not available yet');
      }

      console.log('Referral processed successfully. Referrer:', referrer.id);
      sessionStorage.removeItem('tilted_referral');
    } catch (error) {
      console.error('Error processing referral:', error);
    }
  };

  // Sign in with email/password
  const signIn = async (email, password) => {
    console.log('AuthContext: Starting signInWithPassword...');
    
    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Sign in timed out')), 8000);
    });
    
    // Race between the actual signIn and the timeout
    const signInPromise = supabase.auth.signInWithPassword({
      email,
      password
    });
    
    try {
      const response = await Promise.race([signInPromise, timeoutPromise]);
      
      console.log('AuthContext: signInWithPassword completed', response);
      
      if (response.error) {
        console.error('AuthContext: signIn error', response.error);
        throw response.error;
      }
      
      console.log('AuthContext: signIn successful, returning data');
      return response.data;
    } catch (err) {
      console.error('AuthContext: signIn caught error', err);
      throw err;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithOAuthNative('google');
      if (result?.error) {
        console.error('Google OAuth error:', result.error);
        if (result.error.message?.includes('provider') || result.error.message?.includes('not enabled')) {
          throw new Error('Google sign-in is not available yet. Please use email/password to sign in.');
        }
        throw new Error(`Google sign-in failed: ${result.error.message}`);
      }
      return result?.data;
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code === OAUTH_CANCELLED || err.message?.includes('Google sign-in')) {
        throw err;
      }
      throw new Error('Google sign-in is temporarily unavailable. Please use email/password.');
    }
  };

  // Sign in with Apple
  const signInWithApple = async () => {
    try {
      const result = await signInWithOAuthNative('apple');
      if (result?.error) {
        console.error('Apple OAuth error:', result.error);
        if (result.error.message?.includes('provider') || result.error.message?.includes('not enabled')) {
          throw new Error('Apple sign-in is not available yet. Please use email/password to sign in.');
        }
        throw new Error(`Apple sign-in failed: ${result.error.message}`);
      }
      return result?.data;
    } catch (err) {
      console.error('Apple sign-in error:', err);
      if (err.code === OAUTH_CANCELLED || err.message?.includes('Apple sign-in')) {
        throw err;
      }
      throw new Error(`Apple sign-in failed: ${err.message || 'unknown error'}`);
    }
  };

  // Sign out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    resetUser();
    removeExternalUserId();

    // Clear session storage for loading state management
    sessionStorage.removeItem('tilted_initial_load_complete');
    sessionStorage.removeItem('tilted_last_fetch');
    setCachedProfile(null);

    setUser(null);
    setProfile(null);
    setAgeVerified(false);
  };

  // Update profile
  const updateProfile = async (updates) => {
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    setCachedProfile(data);
    await logActivity(user.id, 'profile_update');
    return data;
  };

  // Change email
  const changeEmail = async (newEmail, password) => {
    if (!user) throw new Error('Not authenticated');

    // First verify the password by trying to sign in
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password
    });

    if (verifyError) {
      throw new Error('Invalid password');
    }

    // Update email
    const { error } = await supabase.auth.updateUser({
      email: newEmail
    });

    if (error) throw error;

    await logActivity(user.id, 'email_change');
    return true;
  };

  // Verify age (for users who signed up without age verification)
  const verifyAge = async (birthdate) => {
    if (!user) throw new Error('Not authenticated');

    const age = calculateAge(birthdate);
    if (age < 21) {
      throw new Error('You must be 21 or older to use Tilted.');
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({
        birthdate,
        age_verified: true
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    setCachedProfile(data);
    setAgeVerified(true);
    return data;
  };

  // Accept Terms of Service
  const acceptTerms = async () => {
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .update({ tos_accepted_at: new Date().toISOString() })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    setCachedProfile(data);
    return data;
  };

  // Upload profile picture
  const uploadProfilePicture = async (file) => {
    if (!user) throw new Error('Please sign in to upload a profile picture');

    // Validate file exists
    if (!file) throw new Error('No file selected');

    // Validate file size
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      throw new Error(`File is too large (${sizeMB}MB). Maximum size is 5MB.`);
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.type}. Please use JPG, PNG, or WebP.`);
    }

    try {
      // Delete old profile picture if exists
      if (profile?.avatar_url) {
        try {
          const urlParts = profile.avatar_url.split('/');
          const oldFileName = urlParts[urlParts.length - 1];
          if (oldFileName && oldFileName !== 'undefined') {
            await supabase.storage.from('avatars').remove([`${user.id}/${oldFileName}`]);
          }
        } catch (deleteErr) {
          console.warn('Could not delete old avatar:', deleteErr);
          // Continue with upload even if delete fails
        }
      }

      // Create unique file path
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      console.log('Uploading to:', fileName);

      // Convert File to Blob for iOS WKWebView compatibility
      const uploadBlob = await prepareFileForUpload(file);

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, uploadBlob, { 
          upsert: true,
          cacheControl: '3600'
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        if (uploadError.message?.includes('bucket') || uploadError.message?.includes('not found')) {
          throw new Error('Storage is not configured. Please contact support.');
        }
        if (uploadError.message?.includes('policy') || uploadError.message?.includes('permission')) {
          throw new Error('Upload not allowed. Please try again or contact support.');
        }
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      console.log('Upload successful:', uploadData);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Could not get image URL after upload');
      }

      // Add cache-busting timestamp to URL to prevent browser caching issues
      const avatarUrlWithCacheBust = `${urlData.publicUrl}?t=${Date.now()}`;
      console.log('Public URL with cache bust:', avatarUrlWithCacheBust);

      // Update profile with new avatar URL
      const { data, error } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrlWithCacheBust })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Profile update error:', error);
        throw new Error('Could not save profile picture. Please try again.');
      }

      setProfile(data);
      setCachedProfile(data);
      
      // Force a refresh of all user data to ensure avatar is updated everywhere
      window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { avatarUrl: avatarUrlWithCacheBust } }));
      await logActivity(user.id, 'avatar_update');
      return data;
    } catch (err) {
      console.error('Profile picture upload failed:', err);
      throw err;
    }
  };

  // Delete account
  const deleteAccount = async (password) => {
    if (!user) throw new Error('Not authenticated');

    // Verify password first
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password
    });

    if (verifyError) {
      throw new Error('Invalid password');
    }

    // Delete user data (profile will cascade delete ratings, comments, lists)
    const { error: deleteError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', user.id);

    if (deleteError) throw deleteError;

    // Delete auth record for full Apple-compliant account deletion
    try {
      await supabase.rpc('delete_own_account');
    } catch (e) {
      console.warn('Could not delete auth record (RPC may not exist yet):', e.message);
    }

    // Sign out
    await signOut();

    return true;
  };

  // Check if user is admin
  const isAdmin = profile?.is_admin === true;

  const value = {
    user,
    profile,
    loading,
    isAdmin,
    ageVerified,
    isNewUser,
    isUsernameReserved,
    calculateAge,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    updateProfile,
    changeEmail,
    verifyAge,
    acceptTerms,
    uploadProfilePicture,
    deleteAccount,
    logActivity,
    refreshProfile: async () => {
      if (user) {
        const profile = await fetchProfile(user.id);
        setProfile(profile);
        setCachedProfile(profile);
        setAgeVerified(profile?.age_verified || false);
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
